import { object, string, number, mixed } from 'yup'

export const defaultCrossSectionFormValues = (): TimberCrossSection => {
  return {
    shape: {
      height: 10,
      width: 10,
      kind_literal: 'RectangularCS',
    },
    material: {
      kind: 'Softwood',
      identifier: 'C24',
    } as TimberMaterial,
    usage_class: 'One',
    kind: 'Timber',
  }
}

// Shape schema based on the type of material
const rectangularCSShapeSchema = object({
  kind_literal: string().oneOf(['RectangularCS']).required(),
  // TODO: it should be strictly larger than 0, but this does not work yet
  width: number().required().min(0, 'Width must be non-negative'),
  height: number().required().min(0, 'Height must be non-negative'),
})

export const steelCSShapeSchema = object({
  kind_literal: string().oneOf(['SteelCS']).required(),
  profile: string().required(),
  profile_type: string()
    .oneOf(['IAndHSection', 'RROAndQROSection', 'ROSection', 'UPNSection', 'UPESection'])
    .required(),
  // all steel cross section have height and width except for the RO section
  height: number().when('profile_type', {
    is: (profile_type: string) => profile_type === 'ROSection',
    then: schema => schema.optional(),
    otherwise: schema => schema.required().positive('Height must be greater than 0'),
  }),
  width: number().when('profile_type', {
    is: (profile_type: string) => profile_type === 'ROSection',
    then: schema => schema.optional(),
    otherwise: schema => schema.required().positive('Width must be greater than 0'),
  }),
  // RO section has a diameter and thickness instead of height and width
  diameter: number().when('profile_type', {
    is: (profile_type: string) => profile_type === 'ROSection',
    then: schema => schema.required().positive('Diameter must be greater than 0'),
    otherwise: schema => schema.optional(),
  }),
  thickness: number().when('profile_type', {
    is: (profile_type: string) => profile_type === 'ROSection',
    then: schema => schema.required().positive('Thickness must be greater than 0'),
    otherwise: schema => schema.optional(),
  }),
})

export const defaultSteelCSShape = {
  profile: 'IPE 80',
  area: 0.000764,
  shear_area_y: 0.0004784,
  shear_area_z: 0.00035736,
  area_moment_inertia_my: 8.01e-7,
  area_moment_inertia_mz: 8.49e-8,
  resistance_moment_my: 0.00002,
  resistance_moment_mz: 0.00000369,
  plastic_resistance_moment_my: 0.00002322,
  plastic_resistance_moment_mz: 0.000005818,
  radius_of_gyration_my: 0.032379458,
  radius_of_gyration_mz: 0.010541615,
  torsion_moment_inertia: 6.98e-9,
  kind_literal: 'SteelCS',
  height: 0.08,
  width: 0.046,
  thickness_web: 0.0038,
  height_web: 0.0696,
  height_flat_web: 0.0596,
  thickness_flange: 0.0052,
  radius: 0.005,
  profile_type: 'IAndHSection',
} as SteelCSShape

const materialWithIdentifierSchema = object({
  kind: string().oneOf(['Glulam', 'Softwood', 'LVL', 'Steel']).required(),
  identifier: string().required(),
})

const crossSectionShapeSchema = mixed().test('shape-validation', 'Invalid shape', function (value) {
  if (!value) return false

  switch (value.kind_literal) {
    case 'RectangularCS':
      return rectangularCSShapeSchema.isValidSync(value)
    case 'SteelCS':
      return steelCSShapeSchema.isValidSync(value)
    default:
      return false
  }
})

export const crossSectionSchema = object({
  material: materialWithIdentifierSchema.required(),
  materialType: string()
    .oneOf(['softwoodMaterial', 'glulamMaterial', 'lvlMaterial', 'steelMaterial'])
    .required(),
  shape: crossSectionShapeSchema.required(),
  usage_class: string()
    .oneOf(['One', 'Two', 'Three'])
    .when('materialType', {
      is: (materialType: string) =>
        materialType === 'softwoodMaterial' ||
        materialType === 'glulamMaterial' ||
        materialType === 'lvlMaterial',
      then: schema => schema.required(),
      otherwise: schema => schema.optional().nullable(),
    }),
  steelShape: steelCSShapeSchema.optional().nullable(),
})

export const crossSectionFormSchema = object({
  crossSection: crossSectionSchema.required(),
})

export interface CrossSectionFormDataType {
  material: Material
  materialType: MaterialType
  shape?: CrossSectionShape
  usage_class?: UsageClass
  steelShape?: SteelCSShape
}

export const crossSectionKindFromMaterialType = (
  materialType: MaterialType,
): MemberCrossSectionKind => {
  return materialType === 'steelMaterial' ? 'Steel' : 'Timber'
}

type CrossSectionFormDataToPayloadOptions = {
  strict?: boolean
}

export function crossSectionFormDataToPayload(
  formData: CrossSectionFormDataType,
  options: { strict: true },
): CrossSection
export function crossSectionFormDataToPayload(
  formData: CrossSectionFormDataType,
  options?: CrossSectionFormDataToPayloadOptions,
): CrossSection | undefined
export function crossSectionFormDataToPayload(
  formData: CrossSectionFormDataType,
  options: CrossSectionFormDataToPayloadOptions = {},
): CrossSection | undefined {
  const { strict = false } = options
  const kind = crossSectionKindFromMaterialType(formData.materialType)

  if (kind === 'Steel') {
    if (formData.material.kind !== 'Steel') {
      if (strict) throw new Error('Steel cross sections require steel materials')
      return undefined
    }
    if (!formData.steelShape || typeof formData.steelShape === 'string') {
      if (strict) throw new Error('Steel cross sections require a steel shape selection')
      return undefined
    }

    return {
      kind: 'Steel',
      material: formData.material as SteelMaterial,
      shape: formData.steelShape,
    }
  }

  if (formData.material.kind === 'Steel') {
    if (strict) throw new Error('Timber cross sections cannot use steel materials')
    return undefined
  }
  if (!formData.shape || formData.shape.kind_literal !== 'RectangularCS') {
    if (strict) throw new Error('Timber cross sections require a rectangular shape')
    return undefined
  }
  if (!formData.usage_class) {
    if (strict) throw new Error('Timber cross sections require a usage class')
    return undefined
  }

  return {
    kind: 'Timber',
    material: formData.material as TimberMaterial,
    shape: formData.shape as RectangularCSShape,
    usage_class: formData.usage_class,
  }
}
